import { useEffect } from 'react';

import invariant from 'tiny-invariant';

import {
	attachClosestEdge,
	type Edge,
	extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
	draggable,
	dropTargetForElements,
	type ElementDropTargetGetFeedbackArgs,
	type ElementMonitorGetFeedbackArgs,
	monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

export interface DragAndDropConfig<
	TData extends Record<string, unknown> = Record<string, unknown>,
> {
	/** Whether drag and drop is enabled */
	enabled: boolean;

	/** The element ref to attach drag behavior to */
	elementRef: React.RefObject<HTMLElement>;

	/** The drop target ref for showing/hiding drop indicators */
	dropTargetRef?: React.RefObject<HTMLElement>;

	/** Allowed edges for drop indicators */
	allowedEdges: Edge[];

	/** Data to attach to the draggable element */
	getData: () => TData;

	/** Function to check if a source can be dropped on this element */
	canDrop?: (args: ElementDropTargetGetFeedbackArgs) => boolean;

	/** Function to check if this element can monitor drag operations */
	canMonitor?: (args: ElementMonitorGetFeedbackArgs) => boolean;

	/** Callback when drag starts */
	onDragStart?: () => void;

	/** Callback when dragging over this element */
	onDragEnter?: (edge: Edge | null) => void;

	/** Callback when dragging within this element */
	onDrag?: (
		edge: Edge | null,
		sourceElement: HTMLElement,
		targetElement: HTMLElement,
		sourceData?: Record<string, unknown>,
	) => void;

	/** Callback when drag leaves this element */
	onDragLeave?: () => void;

	/** Callback when drop occurs on this element */
	onDrop?: () => void;

	/** Callback when any drag operation completes */
	onDragEnd?: () => void;

	/** Callback when drag preview is generated */
	onGenerateDragPreview?: () => void;

	/** Additional dependencies for the effect */
	dependencies?: any[];
}

/**
 * Custom hook that consolidates drag-and-drop logic for both row and column reordering
 */
export function usePragmaticDragAndDrop<
	TData extends Record<string, unknown> = Record<string, unknown>,
>({
	enabled,
	elementRef,
	dropTargetRef,
	allowedEdges,
	getData,
	canDrop,
	canMonitor,
	onDragStart,
	onDragEnter,
	onDrag,
	onDragLeave,
	onDrop,
	onDragEnd,
	onGenerateDragPreview,
	dependencies = [],
}: DragAndDropConfig<TData>): void {
	useEffect(() => {
		if (!enabled) {
			return;
		}

		invariant(elementRef.current);
		const element = elementRef.current;
		invariant(element);

		const data = getData();

		return combine(
			draggable({
				element,
				getInitialData() {
					return data;
				},
				onDragStart: () => {
					onDragStart?.();
				},
				onDrop: () => {
					onDragEnd?.();
					if (dropTargetRef?.current) {
						dropTargetRef.current.style.display = 'none';
					}
				},
			}),
			dropTargetForElements({
				element,
				...(canDrop && {
					canDrop: canDrop,
				}),
				getData: ({ input }) => {
					return attachClosestEdge(data, {
						input,
						element,
						allowedEdges,
					});
				},
				onDragEnter(args) {
					const edge = extractClosestEdge(args.self.data);
					onDragEnter?.(edge);
				},
				onDrag(args) {
					if (dropTargetRef?.current) {
						dropTargetRef.current.style.display = 'block';
					}
					const closestEdge: Edge | null = extractClosestEdge(args.self.data);
					onDrag?.(closestEdge, args.source.element, element, args.source.data);
				},
				onDragLeave() {
					onDragLeave?.();
				},
				onDrop() {
					onDrop?.();
				},
			}),
			monitorForElements({
				...(canMonitor && {
					canMonitor: canMonitor,
				}),
				onGenerateDragPreview() {
					onGenerateDragPreview?.();
					if (dropTargetRef?.current) {
						dropTargetRef.current.style.display = 'block';
					}
				},
				onDrop() {
					onDragEnd?.();
					if (dropTargetRef?.current) {
						dropTargetRef.current.style.display = 'none';
					}
				},
			}),
		);
	}, [
		enabled,
		elementRef,
		dropTargetRef,
		getData,
		canDrop,
		canMonitor,
		onDragStart,
		onDragEnter,
		onDrag,
		onDragLeave,
		onDrop,
		onDragEnd,
		onGenerateDragPreview,
		allowedEdges,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		...dependencies,
	]);
}
